/**
 * Matrix / colour-transform / rectangle helpers for the Canvas renderer (pixels, doubles).
 * Matrices are [a, b, c, d, tx, ty] (x' = a*x + c*y + tx, y' = b*x + d*y + ty), colour transforms
 * [rm, gm, bm, am, ro, go, bo, ao] (multipliers as reals, offsets in 0..255 units), both as in
 * src/flash/display/render-data.ts.
 */

export type Mat = [number, number, number, number, number, number];
export type CT = [number, number, number, number, number, number, number, number];

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const IDENTITY_MAT: Mat = [1, 0, 0, 1, 0, 0];
/** Shared constant: never mutate. */
export const IDENTITY_CT: CT = [1, 1, 1, 1, 0, 0, 0, 0];

/** m2 * m1 (apply m1 first, then m2). */
export function mul(m2: Readonly<Mat>, m1: Readonly<Mat>): Mat {
  return [
    m2[0] * m1[0] + m2[2] * m1[1],
    m2[1] * m1[0] + m2[3] * m1[1],
    m2[0] * m1[2] + m2[2] * m1[3],
    m2[1] * m1[2] + m2[3] * m1[3],
    m2[0] * m1[4] + m2[2] * m1[5] + m2[4],
    m2[1] * m1[4] + m2[3] * m1[5] + m2[5],
  ];
}

export function translate(m: Readonly<Mat>, dx: number, dy: number): Mat {
  return [m[0], m[1], m[2], m[3], m[0] * dx + m[2] * dy + m[4], m[1] * dx + m[3] * dy + m[5]];
}

export function invert(m: Readonly<Mat>): Mat | null {
  const det = m[0] * m[3] - m[1] * m[2];
  if (!det || !Number.isFinite(det)) return null;
  const a = m[3] / det;
  const b = -m[1] / det;
  const c = -m[2] / det;
  const d = m[0] / det;
  return [a, b, c, d, -(a * m[4] + c * m[5]), -(b * m[4] + d * m[5])];
}

/** Geometric mean scale of a matrix (sqrt |det|). */
export function matScale(m: Readonly<Mat>): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
}

/** Axis-aligned bounds of the rectangle (x, y, w, h) under matrix m. */
export function rectBox(m: Readonly<Mat>, x: number, y: number, w: number, h: number): Box {
  const x1 = x + w;
  const y1 = y + h;
  const ax = m[0] * x + m[2] * y + m[4];
  const ay = m[1] * x + m[3] * y + m[5];
  const bx = m[0] * x1 + m[2] * y + m[4];
  const by = m[1] * x1 + m[3] * y + m[5];
  const cx = m[0] * x + m[2] * y1 + m[4];
  const cy = m[1] * x + m[3] * y1 + m[5];
  const dx = m[0] * x1 + m[2] * y1 + m[4];
  const dy = m[1] * x1 + m[3] * y1 + m[5];
  return {
    x0: Math.min(ax, bx, cx, dx),
    y0: Math.min(ay, by, cy, dy),
    x1: Math.max(ax, bx, cx, dx),
    y1: Math.max(ay, by, cy, dy),
  };
}

export function boxIntersects(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

export function boxIntersect(a: Box, b: Box): Box {
  return { x0: Math.max(a.x0, b.x0), y0: Math.max(a.y0, b.y0), x1: Math.min(a.x1, b.x1), y1: Math.min(a.y1, b.y1) };
}

export function boxEmpty(b: Box): boolean {
  return !(b.x1 > b.x0 && b.y1 > b.y0);
}

/** Round a box outwards to whole device pixels. */
export function boxRoundOut(b: Box): Box {
  return { x0: Math.floor(b.x0), y0: Math.floor(b.y0), x1: Math.ceil(b.x1), y1: Math.ceil(b.y1) };
}

export function boxPad(b: Box, px: number, py: number = px): Box {
  return { x0: b.x0 - px, y0: b.y0 - py, x1: b.x1 + px, y1: b.y1 + py };
}

/** Colour transform concatenation: apply `inner` first, then `outer` (Flash concatenation). */
export function concatCT(outer: Readonly<CT>, inner: Readonly<CT>): CT {
  return [
    inner[0] * outer[0],
    inner[1] * outer[1],
    inner[2] * outer[2],
    inner[3] * outer[3],
    inner[4] * outer[0] + outer[4],
    inner[5] * outer[1] + outer[5],
    inner[6] * outer[2] + outer[6],
    inner[7] * outer[3] + outer[7],
  ];
}

export function isIdentityCT(ct: Readonly<CT>): boolean {
  return ct[0] === 1 && ct[1] === 1 && ct[2] === 1 && ct[3] === 1 && ct[4] === 0 && ct[5] === 0 && ct[6] === 0 && ct[7] === 0;
}

/** True when the transform only scales alpha by a factor in [0, 1] (drawable with globalAlpha). */
export function isAlphaOnlyCT(ct: Readonly<CT>): boolean {
  return ct[0] === 1 && ct[1] === 1 && ct[2] === 1 && ct[4] === 0 && ct[5] === 0 && ct[6] === 0 && ct[7] === 0 && ct[3] >= 0 && ct[3] <= 1;
}

/** Nothing drawn with this transform can be visible (alpha forced to 0). */
export function isInvisibleCT(ct: Readonly<CT>): boolean {
  return ct[3] <= 0 && ct[7] <= 0;
}

export function ctKey(ct: Readonly<CT>): string {
  let s = '';
  for (let i = 0; i < 8; i++) s += (i ? ',' : '') + (Math.round(ct[i] * 4096) / 4096);
  return s;
}

function clamp255(v: number): number {
  return v <= 0 ? 0 : v >= 255 ? 255 : v;
}

/** Colour matrix (Flash ColorMatrixFilter layout: 4 rows of 5, offsets in 0..255 units). */
export type CM = readonly number[];

/**
 * Colour stages applied to a leaf AFTER its (concatenated) colour transform, outermost first:
 * colour transforms (length 8) and colour matrices (length 20). Built by the renderer when a
 * ColorMatrixFilter is distributed to the leaves of a container (see `isDistributableMatrix`); the
 * leaf applies its CT, then the stages from last to first, clamping after each stage like Flash.
 */
export type ColorStages = ReadonlyArray<CT | CM>;

/**
 * True when applying `m` to every leaf of a container gives the same pixels as applying it to the
 * composited container (what Flash does): alpha is passed through unchanged (row 4 = [0,0,0,1,0])
 * and the colour rows do not read alpha (m[3] = m[8] = m[13] = 0). The colour rows are then affine
 * in premultiplied space, which commutes with source-over compositing; only the per-stage clamping
 * of overlapping semi-transparent pixels can differ. The game's hue filters (red water, napalm)
 * have this form.
 */
export function isDistributableMatrix(m: CM): boolean {
  return m.length >= 20 && m[15] === 0 && m[16] === 0 && m[17] === 0 && m[18] === 1 && m[19] === 0 && m[3] === 0 && m[8] === 0 && m[13] === 0;
}

export function isIdentityMatrix(m: CM): boolean {
  for (let i = 0; i < 20; i++) if (m[i] !== (i % 6 === 0 ? 1 : 0)) return false;
  return true;
}

export function stagesKey(stages: ColorStages | null | undefined): string {
  if (!stages || !stages.length) return '';
  let s = '';
  for (const st of stages) {
    s += st.length === 8 ? 'c' : 'm';
    for (let i = 0; i < st.length; i++) s += (i ? ',' : '') + Math.round(st[i] * 65536) / 65536;
    s += ';';
  }
  return s;
}

/** Straight RGBA (0..255, floats) through one stage, clamped. */
function stage4(st: CT | CM, v: [number, number, number, number]): void {
  const [r, g, b, a] = v;
  if (st.length === 8) {
    v[0] = clamp255(r * st[0] + st[4]);
    v[1] = clamp255(g * st[1] + st[5]);
    v[2] = clamp255(b * st[2] + st[6]);
    v[3] = clamp255(a * st[3] + st[7]);
  } else {
    v[0] = clamp255(st[0] * r + st[1] * g + st[2] * b + st[3] * a + st[4]);
    v[1] = clamp255(st[5] * r + st[6] * g + st[7] * b + st[8] * a + st[9]);
    v[2] = clamp255(st[10] * r + st[11] * g + st[12] * b + st[13] * a + st[14]);
    v[3] = clamp255(st[15] * r + st[16] * g + st[17] * b + st[18] * a + st[19]);
  }
}

/** CSS colour of RGB `rgb` with alpha `alpha` (0..1) after colour transform `ct` and the outer stages. */
export function cssColor(rgb: number, alpha: number, ct: Readonly<CT> = IDENTITY_CT, outer?: ColorStages | null): string {
  const v: [number, number, number, number] = [(rgb >>> 16) & 255, (rgb >>> 8) & 255, rgb & 255, Math.max(0, Math.min(1, alpha)) * 255];
  stage4(ct, v);
  if (outer) for (let i = outer.length - 1; i >= 0; i--) stage4(outer[i], v);
  return `rgba(${Math.round(v[0])},${Math.round(v[1])},${Math.round(v[2])},${Math.round((v[3] / 255) * 1000) / 1000})`;
}

/** Nothing drawn with `ct` followed by `outer` can be visible (conservative). */
export function isInvisibleColor(ct: Readonly<CT>, outer: ColorStages | null): boolean {
  if (!isInvisibleCT(ct)) return false;
  if (outer) for (const st of outer) if (st.length === 8 && st[7] > 0) return false;
  return true;
}

/**
 * Apply a colour transform in place to straight RGBA pixels (Flash applies colour transforms to
 * unpremultiplied colour). `keepTransparent`: fully transparent pixels stay transparent (shape
 * rasters: pixels outside the vector fill are not part of the shape).
 */
/** Apply a colour matrix in place to straight RGBA pixels (Uint8ClampedArray clamps each channel). */
export function applyCMToRGBA(data: Uint8ClampedArray, m: CM, keepTransparent: boolean): void {
  const [m0, m1, m2, m3, m4, m5, m6, m7, m8, m9, m10, m11, m12, m13, m14, m15, m16, m17, m18, m19] = m;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0 && keepTransparent) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    data[i] = m0 * r + m1 * g + m2 * b + m3 * a + m4;
    data[i + 1] = m5 * r + m6 * g + m7 * b + m8 * a + m9;
    data[i + 2] = m10 * r + m11 * g + m12 * b + m13 * a + m14;
    data[i + 3] = m15 * r + m16 * g + m17 * b + m18 * a + m19;
  }
}

/** Apply `ct`, then the outer stages (innermost = last first), in place to straight RGBA pixels. */
export function applyColorToRGBA(data: Uint8ClampedArray, ct: Readonly<CT>, outer: ColorStages | null | undefined, keepTransparent: boolean): void {
  if (!isIdentityCT(ct)) applyCTToRGBA(data, ct, keepTransparent);
  if (!outer) return;
  for (let i = outer.length - 1; i >= 0; i--) {
    const st = outer[i];
    if (st.length === 8) {
      if (!isIdentityCT(st as CT)) applyCTToRGBA(data, st as CT, keepTransparent);
    } else if (!isIdentityMatrix(st)) applyCMToRGBA(data, st, keepTransparent);
  }
}

export function applyCTToRGBA(data: Uint8ClampedArray, ct: Readonly<CT>, keepTransparent: boolean): void {
  const [rm, gm, bm, am, ro, go, bo, ao] = ct;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0 && keepTransparent) continue;
    data[i] = data[i] * rm + ro;
    data[i + 1] = data[i + 1] * gm + go;
    data[i + 2] = data[i + 2] * bm + bo;
    data[i + 3] = a * am + ao;
  }
}
